import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ExternalLink,
  Sparkles,
  Star,
  Mail,
  BookOpen,
  Heart,
} from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { canonicalUrl } from "../data/seo";
import { featuredSlugs, amazonUrl, isComingSoon } from "../data/books";
import { useBooks } from "../context/BooksContext";
import { Logo } from "../components/Logo";
import BookCard from "../components/BookCard";
import BookCover from "../components/BookCover";
import { AUTHOR_PHOTO } from "../data/site";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";

export default function Home() {
  usePageMeta({
    title: "Digsby Early Learning | Fun, Educational Books for Growing Minds",
    description:
      "Preschool-to-first-grade workbooks and story books by Dr. Maydine Alcid-Digsby, a 30+ year early childhood education veteran. Shop on Amazon.",
    canonical: canonicalUrl("/"),
  });

  const { books, getBook, featuredBook } = useBooks();
  const featured = featuredSlugs.map(getBook).filter(Boolean);
  const comingSoonBooks = books.filter((b) => b.status === "coming_soon");
  const spotlightComingSoon = isComingSoon(featuredBook);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter`,
        { email, first_name: firstName || undefined }
      );
      toast.success(data.message || "Thanks for subscribing!", {
        description: "Welcome to the Digsby Early Learning family.",
      });
      setFirstName("");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FFFBF2]">
        <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-[#FBEF14]/30 blur-3xl" aria-hidden />
        <div className="absolute -bottom-24 -right-10 w-80 h-80 rounded-full bg-[#00EE00]/20 blur-3xl" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <div className="mb-6">
              <Logo height={192} />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 px-4 py-1.5 text-sm font-bold text-slate-700 mb-6">
              <Sparkles size={16} className="text-[#FF0000]" /> Ages 2–7 · Preschool to First Grade
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-black">
              Fun, Educational Books for{" "}
              <span className="text-[#0000FF]">Growing Minds</span>
            </h1>
            <p className="mt-6 text-lg text-slate-700 max-w-xl">
              Colorful workbooks and heartwarming story books that build early skills,
              confidence, and a lifelong love of learning, thoughtfully created by an
              early childhood education expert.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-books"
                className="inline-flex items-center gap-2 min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
              >
                Shop the Books <ArrowRight size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-meet-author"
                className="inline-flex items-center gap-2 min-h-[52px] rounded-full bg-white border-2 border-black text-black font-bold text-lg px-8 hover:bg-[#FBEF14] hover:-translate-y-1 transition-all duration-200"
              >
                Meet the Author
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rotate-3 rounded-[2.5rem] bg-[#0000FF]/10" aria-hidden />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/713f36d8-0e53-4893-8c92-47f083000495/images/74a10cbfcf7742d1bd3c9b1325e6b286aab42f7fca88174d140790f728c36ce4.png"
              alt="A diverse, multicultural group of happy young children reading Digsby Early Learning books together"
              loading="eager"
              className="relative rounded-[2.5rem] border-4 border-black shadow-[8px_8px_0px_#000] w-full object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#0000FF] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Star size={22} className="text-[#FBEF14] fill-[#FBEF14]" />
          <p className="font-heading font-bold text-lg sm:text-xl">
            Written by a 30+ year early childhood education veteran
          </p>
          <Star size={22} className="text-[#FBEF14] fill-[#FBEF14]" />
        </div>
      </section>

      {/* Featured spotlight: The Readiness Ecosystem */}
      {featuredBook && (
        <section id="featured-book" className="relative overflow-hidden bg-white border-y-2 border-black/5 scroll-mt-24">
          <div className="absolute -top-20 right-0 w-80 h-80 rounded-full bg-[#0000FF]/10 blur-3xl" aria-hidden />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 items-center">
            <div className="relative flex justify-center">
              {spotlightComingSoon && (
                <span
                  data-testid="spotlight-coming-soon-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black text-white px-4 py-1.5 text-xs font-bold shadow-md whitespace-nowrap"
                >
                  ✨ Coming Soon
                </span>
              )}
              <Link
                to={`/books/${featuredBook.slug}`}
                data-testid="feature-cover-link"
                className="block w-full max-w-sm aspect-[2/3] rounded-[1.5rem] overflow-hidden border-4 border-black shadow-[10px_10px_0px_#FBEF14]"
              >
                <BookCover book={featuredBook} />
              </Link>
            </div>
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0000FF] uppercase tracking-wider">
                <Star size={16} className="fill-[#0000FF]" /> Featured · The Next Chapter from Dr. Maydine Alcid-Digsby
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl mt-3 text-black leading-tight">
                The Readiness Ecosystem
              </h2>
              <p className="mt-2 font-heading font-bold text-lg text-[#00A000]">
                Reimagining Kindergarten Readiness Through Shared Responsibility
              </p>
              <p className="mt-5 text-lg text-slate-700">
                What if kindergarten readiness is not something children achieve alone, but
                something adults build together? Dr. Alcid-Digsby introduces a powerful new
                framework that redefines readiness as a shared responsibility among children,
                families, educators, schools, and the systems that support them.
              </p>
              <p className="mt-4 font-heading font-bold text-slate-800">
                {spotlightComingSoon
                  ? "Join the Learning Circle and be among the first to know when the book launches."
                  : "Now available, grab your copy on Amazon!"}
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                {spotlightComingSoon ? (
                  <Link
                    to="/#learning-circle"
                    data-testid="feature-join-waitlist"
                    className="inline-flex items-center gap-2 min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                  >
                    Join the Learning Circle <ArrowRight size={20} />
                  </Link>
                ) : (
                  <a
                    href={amazonUrl(featuredBook.amazon)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="feature-buy-amazon"
                    className="inline-flex items-center gap-2 min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                  >
                    Buy on Amazon <ExternalLink size={20} />
                  </a>
                )}
                <Link
                  to={`/books/${featuredBook.slug}`}
                  data-testid="feature-learn-more"
                  className="inline-flex items-center gap-2 min-h-[52px] rounded-full bg-white border-2 border-black text-black font-bold text-lg px-8 hover:bg-[#FBEF14] hover:-translate-y-1 transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured carousel */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FF0000] uppercase tracking-wider">
              <BookOpen size={16} /> Best Sellers
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
              Featured Books
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="featured-view-all"
            className="inline-flex items-center gap-1 font-bold text-[#0000FF] hover:gap-2 transition-all"
          >
            View all books <ArrowRight size={18} />
          </Link>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-4">
            {featured.map((book) => (
              <CarouselItem key={book.slug} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                <BookCard book={book} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious data-testid="carousel-prev" className="hidden sm:flex -left-4 border-2 border-black bg-white hover:bg-[#FBEF14]" />
          <CarouselNext data-testid="carousel-next" className="hidden sm:flex -right-4 border-2 border-black bg-white hover:bg-[#FBEF14]" />
        </Carousel>
      </section>

      {/* Coming Soon carousel */}
      {comingSoonBooks.length > 0 && (
        <section id="coming-soon" className="bg-[#FFFBF2] scroll-mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <div>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FF0000] uppercase tracking-wider">
                  <Sparkles size={16} /> On the Way
                </span>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
                  Coming Soon
                </h2>
              </div>
              <Link
                to="/#learning-circle"
                data-testid="coming-soon-join-waitlist"
                className="inline-flex items-center gap-1 font-bold text-[#FF0000] hover:gap-2 transition-all"
              >
                Join the Learning Circle <ArrowRight size={18} />
              </Link>
            </div>

            <Carousel opts={{ align: "start", loop: comingSoonBooks.length > 2 }} className="w-full">
              <CarouselContent className="-ml-4">
                {comingSoonBooks.map((book) => (
                  <CarouselItem key={book.slug} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                    <BookCard book={book} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious data-testid="coming-soon-prev" className="hidden sm:flex -left-4 border-2 border-black bg-white hover:bg-[#FBEF14]" />
              <CarouselNext data-testid="coming-soon-next" className="hidden sm:flex -right-4 border-2 border-black bg-white hover:bg-[#FBEF14]" />
            </Carousel>
          </div>
        </section>
      )}

      {/* Meet the author teaser */}
      <section className="bg-[#FFFBF2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 items-center">
          <div className="relative order-2 lg:order-1">
            <img
              src={AUTHOR_PHOTO}
              alt="Dr. Maydine L. Alcid-Digsby, author and early childhood education leader"
              loading="lazy"
              className="rounded-[2rem] border-4 border-black shadow-[8px_8px_0px_#00EE00] w-full max-w-md object-cover aspect-[4/5] mx-auto"
            />
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#00EE00] uppercase tracking-wider" style={{ WebkitTextStroke: "0.4px #000" }}>
              <Heart size={16} className="text-[#FF0000]" /> Meet the Author
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
              Dr. Maydine L. Alcid-Digsby
            </h2>
            <p className="mt-5 text-lg text-slate-700">
              An educator, researcher, school leader, author, and advocate with more than
              three decades of experience advancing excellence in early childhood education.
              Her books reflect a deep belief in the transformative power of learning and
              the importance of nurturing the whole child.
            </p>
            <Link
              to="/about"
              data-testid="teaser-read-more"
              className="mt-7 inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#0000FF] text-white font-bold px-7 hover:bg-[#0000d6] hover:-translate-y-0.5 transition-all duration-200"
            >
              Read her full story <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter signup */}
      <section id="learning-circle" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 scroll-mt-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-black text-white p-8 sm:p-14 text-center">
          <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-[#FF0000]/40 blur-3xl" aria-hidden />
          <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-[#0000FF]/50 blur-3xl" aria-hidden />
          <div className="relative">
            <Mail size={40} className="mx-auto text-[#FBEF14] mb-4" />
            <h2 className="font-heading font-bold text-3xl sm:text-4xl">
              Join the Learning Circle & Book Waitlist
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              Be the first to know when new books launch, plus learning tips and early-reader
              activities delivered straight to your inbox.
            </p>
            <form
              onSubmit={handleSignup}
              data-testid="newsletter-form"
              className="mt-8 max-w-lg mx-auto"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="newsletter-email-input"
                className="w-full mb-3 min-h-[52px] rounded-full px-6 text-black font-medium outline-none focus:ring-4 focus:ring-[#FBEF14]"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  data-testid="newsletter-firstname-input"
                  className="flex-1 min-h-[52px] rounded-full px-6 text-black font-medium outline-none focus:ring-4 focus:ring-[#FBEF14]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit"
                  className="min-h-[52px] rounded-full bg-[#FBEF14] text-black font-bold px-8 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60"
                >
                  {submitting ? "Subscribing..." : "Subscribe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
